/**
 * How a piece of knowledge should be presented.
 *
 * Two independent things matter to a reader, and collapsing them into one badge is how products
 * end up showing a machine's guess as though a person had confirmed it:
 *
 *   origin — where it came from: a person said it, a file said it, an operator typed it
 *   state  — how far it has got: still needs confirming, confirmed, contested, rejected
 *
 * Nothing derived from a document is ever labelled as confirmed.
 */

import type { KnowledgeFact } from "./types";

export type FactState =
  "verified" | "needs_confirmation" | "conflicting" | "rejected";
export type FactOrigin = "reported" | "extracted" | "entered" | "derived";

export interface FactStatus {
  state: FactState;
  stateLabel: string;
  stateTone: "ok" | "warn" | "danger" | "neutral";
  origin: FactOrigin;
  originLabel: string;
  originHint: string;
  /** True when this must not be relied on without a human looking at it. */
  provisional: boolean;
}

const STATE_LABEL: Record<FactState, string> = {
  // "Human confirmed", not "Verified": a person here agreed this is what they said. It is NOT
  // official company context — that is Enterprise Context's decision, shown by GovernanceBadge.
  verified: "Human confirmed",
  needs_confirmation: "Needs confirmation",
  conflicting: "Conflicting",
  rejected: "Rejected",
};

const STATE_TONE: Record<FactState, FactStatus["stateTone"]> = {
  verified: "ok",
  needs_confirmation: "warn",
  conflicting: "danger",
  rejected: "neutral",
};

const ORIGIN_LABEL: Record<FactOrigin, string> = {
  reported: "Reported",
  extracted: "Extracted",
  entered: "Entered",
  derived: "Derived",
};

const ORIGIN_HINT: Record<FactOrigin, string> = {
  reported: "Someone told us this in an interview.",
  extracted: "Read out of a file you uploaded. Nobody has checked it yet.",
  entered: "Typed in directly by an operator.",
  derived: "Worked out from other answers.",
};

export function factOrigin(
  fact: Pick<KnowledgeFact, "source_type" | "extractor">,
): FactOrigin {
  if (fact.source_type === "survey_response") return "reported";
  if (
    fact.source_type === "uploaded_source" ||
    fact.source_type === "integration"
  )
    return "extracted";
  if (fact.source_type === "derived") return "derived";
  return "entered";
}

export function factState(
  fact: Pick<KnowledgeFact, "verification_status_code">,
  options: { conflicting?: boolean } = {},
): FactState {
  if (
    fact.verification_status_code === "rejected" ||
    fact.verification_status_code === "superseded"
  ) {
    return "rejected";
  }
  // A contested fact reads as conflicting even once someone has confirmed it, because the
  // disagreement is the thing the reader needs to act on.
  if (options.conflicting || fact.verification_status_code === "disputed")
    return "conflicting";
  if (fact.verification_status_code === "verified") return "verified";
  return "needs_confirmation";
}

export function factStatus(
  fact: Pick<
    KnowledgeFact,
    "verification_status_code" | "source_type" | "extractor"
  >,
  options: { conflicting?: boolean } = {},
): FactStatus {
  const state = factState(fact, options);
  const origin = factOrigin(fact);
  return {
    state,
    stateLabel: STATE_LABEL[state],
    stateTone: STATE_TONE[state],
    origin,
    originLabel: ORIGIN_LABEL[origin],
    originHint: ORIGIN_HINT[origin],
    provisional: state !== "verified",
  };
}

export interface ReviewBuckets<T> {
  confirmed: T[];
  needsReview: T[];
  conflicting: T[];
  rejected: T[];
}

/** Split facts into the four groups the review screen is built around. */
export function bucketFacts<
  T extends Pick<
    KnowledgeFact,
    "id" | "verification_status_code" | "source_type" | "extractor"
  >,
>(
  facts: T[],
  conflictingIds: ReadonlySet<string> = new Set(),
): ReviewBuckets<T> {
  const buckets: ReviewBuckets<T> = {
    confirmed: [],
    needsReview: [],
    conflicting: [],
    rejected: [],
  };
  for (const fact of facts) {
    const state = factState(fact, { conflicting: conflictingIds.has(fact.id) });
    if (state === "verified") buckets.confirmed.push(fact);
    else if (state === "conflicting") buckets.conflicting.push(fact);
    else if (state === "rejected") buckets.rejected.push(fact);
    else buckets.needsReview.push(fact);
  }
  return buckets;
}
